import { Flex, Text } from "@/ui";
import { useCallback, useEffect, useState } from "react";
import { getCurrentCameraConfig } from "viewer/store/viewer/selectors";
import { useViewer } from "viewer/store/viewer";
import Icon from "@/components/Icon";
import ManualVideoIcon from "assets/icons/manual_event.svg?react";
import { useMonitoringPanelButtonStyle } from "@/theme/utils/buttonStyleHooks";

type Props = {
  variant?: "bottom" | "left";
  actionController: ActionController;
};

export default function RecordButton({ variant = "bottom", actionController }: Props) {
  const { getStyleObjAsValues } = useMonitoringPanelButtonStyle();
  const cameraConfig = useViewer(getCurrentCameraConfig);

  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);

  const isRecording = Boolean(cameraConfig?.videoRecordingStatus?.recordingOn);
  const { activeBg, bg, hoverBg } = getStyleObjAsValues(isRecording);

  useEffect(() => {
    const recordingOn = cameraConfig?.videoRecordingStatus?.recordingOn;
    const recordingEnd = recordingOn?.recordingEnd;

    if (!recordingEnd || recordingEnd <= 0) {
      setRemainingSeconds(null);
      return;
    }

    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((recordingEnd - now) / 1000));
      setRemainingSeconds(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [cameraConfig?.videoRecordingStatus?.recordingOn]);

  const toggleRecord = useCallback(() => {
    actionController.map.record.action();
  }, [actionController]);

  const leftPanelVariant = () => {
    return (
      <Flex
        as="button"
        direction="column"
        align="center"
        position="absolute"
        py="8px"
        px="2px"
        minW="60px"
        maxW="60px"
        bg={bg}
        _focusVisible={{
          outline: "2px solid",
          outlineColor: "primary"
        }}
        _hover={{ bg: hoverBg }}
        _active={{ bg: activeBg }}
        _disabled={{
          opacity: 0.4,
          boxShadow: "none",
          pointerEvents: "none"
        }}
        transition="all 0.2s"
        borderRadius="1rem"
        onClick={toggleRecord}
      >
        <Icon icon={ManualVideoIcon} />
        <Text
          w="100%"
          variant="label2"
          align="center"
          color="white"
          mt="4px"
          overflow="hidden"
          display="-webkit-box"
          textOverflow="ellipsis"
          sx={{
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical"
          }}
        >
          {remainingSeconds !== null && `00:${String(remainingSeconds).padStart(2, "0")}`}
        </Text>
      </Flex>
    );
  };

  const bottomPanelVariant = () => {
    return (
      <Flex
        direction="column"
        position="absolute"
        align="center"
        as="button"
        maxW="56px"
        my="0.5rem"
        borderRadius="12px"
        _focusVisible={{
          outline: "2px solid",
          outlineColor: "primary"
        }}
        _hover={{ ".icn-bg": { bg: hoverBg } }}
        _active={{ ".icn-bg": { bg: activeBg } }}
        _disabled={{
          opacity: 0.4,
          boxShadow: "none",
          pointerEvents: "none"
        }}
        onClick={toggleRecord}
      >
        <Flex p="12px" bg={bg} borderRadius="12px" className="icn-bg" transition="all 0.3s" backdropFilter="blur(8px)">
          <Icon icon={ManualVideoIcon} />
        </Flex>
        <Text
          mt="4px"
          variant="label2"
          color="primary.99"
          maxW="100%"
          display="-webkit-box"
          textOverflow="ellipsis"
          overflow="hidden"
          sx={{
            WebkitBoxOrient: "vertical"
          }}
        >
          {remainingSeconds !== null && `00:${String(remainingSeconds).padStart(2, "0")}`}
        </Text>
      </Flex>
    );
  };
  if (!isRecording) return null;
  if (variant === "left") return leftPanelVariant();
  return bottomPanelVariant();
}
